import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/stores/hook";
// Imported (not a "/assets/…" public path) so Vite bundles it — a <4KB PNG is
// inlined as a base64 data URI, so it can never 404 no matter how the host
// serves static files. Used as the fallback for a missing/dead tenant logo.
import xaleLogo from "@/assets/Logo.png";

function MarqueeText({
  text,
  className,
  style,
}: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;
    setShouldScroll(textEl.scrollWidth > container.clientWidth);
  }, [text]);

  return (
    <div ref={containerRef} className="overflow-hidden max-w-[120px]">
      <span
        ref={textRef}
        className={cn(
          "inline-block whitespace-nowrap",
          shouldScroll && "animate-marquee",
          className
        )}
        style={style}
      >
        {text}
      </span>
    </div>
  );
}

export function SidebarHeaderContent({
  /**
   * Renders the suite-switcher affordance (a chevron at the end of the row).
   * Only `SuiteSwitcher` passes this, and only when the user actually has more
   * than one suite — so a single-suite tenant sees this block exactly as it has
   * always looked: no chevron, no hover, no hint that a switcher exists.
   */
  showSuiteChevron = false,
}: {
  showSuiteChevron?: boolean;
}) {
  const { state } = useSidebar();
  const isExpanded = state === "expanded";

  // Defensive read — `currentUser` is `null` between logout and route
  // unmount, and we don't want one render to crash the whole sidebar.
  const user = useAppSelector((state) => state.auth.currentUser?.user);

  const displayName = user?.name?.toLowerCase().includes("system")
    ? user?.tenant?.tenantName
    : user?.name;

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 transition-all duration-200 ease-linear h-full",
        isExpanded ? "justify-start px-[1rem]" : "px-0 gap-0"
      )}
    >
      <div
        className={cn(
          "relative rounded-full shrink-0 flex-shrink-0 transition-all duration-200 ease-linear",
          isExpanded ? "size-[2.75rem]" : "size-[2.5rem]"
        )}
      >
        <img
          alt="Xale Logo"
          className="absolute inset-0 rounded-full size-full object-cover"
          src={user?.tenant?.tenantLogoUrl || xaleLogo}
          onError={(e) => {
            // A tenant's stored `tenantLogoUrl` can be a dead / deleted URL —
            // since it's truthy the `||` fallback above never fires, so the
            // image just breaks. Fall back to the bundled Xale logo on load
            // failure. The `data-fallback` guard makes it fire at most once.
            const img = e.currentTarget;
            if (img.dataset.fallback) return;
            img.dataset.fallback = "1";
            img.src = xaleLogo;
          }}
        />
      </div>
      <div
        aria-hidden={!isExpanded}
        className={cn(
          "flex flex-col items-start justify-center transition-all duration-200 ease-linear min-w-0",
          isExpanded
            ? "opacity-100 translate-x-0 ml-2"
            : "opacity-0 -translate-x-2 max-w-0 ml-0 pointer-events-none"
        )}
      >
        <div className="flex items-center gap-1 whitespace-nowrap min-w-0">
          <span className="text-b1 text-[var(--color-black-10)] shrink-0">
            Hi,
          </span>
          <MarqueeText
            text={displayName || ""}
            className="text-h4"
            style={{
              background:
                "linear-gradient(90deg, color-mix(in srgb, var(--color-black-9) 85%, black 15%) 0%, color-mix(in srgb, var(--color-black-9) 85%, black 15%) 60%, color-mix(in srgb, var(--color-green-9) 85%, black 15%) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          />
        </div>
        <span className="text-b4 text-[var(--color-black-7)]">
          {user?.role?.name}
        </span>
      </div>

      {/* Collapsed rail has no room for it — there the circular logo IS the
          trigger, which is why the switcher lives in this block at all: it is
          the only element that survives both sidebar states. */}
      {showSuiteChevron && isExpanded && (
        <ChevronDown
          aria-hidden="true"
          className="suite-switch-chevron ml-auto h-4 w-4 shrink-0 transition-transform duration-200"
          style={{ color: "var(--color-black-6)" }}
        />
      )}

      <style>{`
        @keyframes marquee {
          0%, 20% { transform: translateX(0); }
          80%, 100% { transform: translateX(calc(-100% + 120px)); }
        }
        .animate-marquee {
          animation: marquee 4s ease-in-out infinite alternate;
        }
        /* Written here rather than as a Tailwind arbitrary value so the
           color-mix() commas can't be mangled by the class parser. */
        .suite-switch-trigger {
          border-radius: 0.625rem;
          transition: background-color 150ms ease;
        }
        .suite-switch-trigger:hover {
          background-color: color-mix(in srgb, var(--color-black-1) 55%, transparent);
        }
        .suite-switch-trigger[data-state="open"] .suite-switch-chevron {
          transform: rotate(180deg);
        }
      `}</style>
    </div>
  );
}
